import React, { useState, useRef, useEffect } from "react";
import {
  Bell,
  Search,
  Menu,
  CheckCheck,
  Wallet,
  X,
  ExternalLink,
  ShoppingCart,
  UserCheck,
  Info,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export interface AdminNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  link?: string;
  data?: Record<string, unknown>;
  icon?: string;
  color?: string;
  priority?: "low" | "normal" | "high" | "urgent";
}

interface TopBarProps {
  onToggleMobileMenu: () => void;
  title: string;
  notifications: AdminNotification[];
  onMarkAllRead: () => void;
  onMarkAsRead: (id: string) => void;
}

export const TopBar: React.FC<TopBarProps> = ({
  onToggleMobileMenu,
  title,
  notifications,
  onMarkAllRead,
  onMarkAsRead,
}) => {
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const unreadCount = notifications.filter((n) => !n.read).length;
  const displayNotifications = notifications.slice(0, 10);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notifRef.current &&
        !notifRef.current.contains(event.target as Node)
      ) {
        setIsNotifOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNotificationClick = (notif: AdminNotification) => {
    onMarkAsRead(notif.id);
    if (notif.link) {
      navigate(notif.link);
    }
    setIsNotifOpen(false);
  };

  const getIconComponent = (iconName?: string, color?: string) => {
    const colorClass = color ? `text-${color}-500` : "text-gray-500";
    const iconProps = { className: `w-4 h-4 ${colorClass}` };

    switch (iconName) {
      case "wallet":
        return <Wallet {...iconProps} />;
      case "shopping-cart":
        return <ShoppingCart {...iconProps} />;
      case "user-check":
        return <UserCheck {...iconProps} />;
      case "check-circle":
        return <CheckCircle {...iconProps} />;
      case "x-circle":
        return <XCircle {...iconProps} />;
      case "clock":
        return <Clock {...iconProps} />;
      case "alert-triangle":
        return <AlertTriangle {...iconProps} />;
      case "info":
        return <Info {...iconProps} />;
      default:
        return <Bell {...iconProps} />;
    }
  };

  const getIconBgClass = (color?: string) => {
    switch (color) {
      case "indigo":
        return "bg-indigo-100";
      case "emerald":
        return "bg-emerald-100";
      case "blue":
        return "bg-blue-100";
      case "red":
        return "bg-red-100";
      case "amber":
        return "bg-amber-100";
      case "orange":
        return "bg-orange-100";
      default:
        return "bg-gray-100";
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Baru saja";
    if (diffMins < 60) return `${diffMins} menit lalu`;
    if (diffHours < 24) return `${diffHours} jam lalu`;
    return `${diffDays} hari lalu`;
  };

  return (
    <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-gray-200 h-16 px-4 md:px-8 flex justify-between items-center">
      <div className="flex items-center gap-4">
        <button
          onClick={onToggleMobileMenu}
          className="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
        >
          <Menu className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold text-gray-800 hidden sm:block">
          {title}
        </h1>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative hidden md:block">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Cari menu atau data..."
            className="pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-giz-primary w-64 transition-all"
          />
        </div>

        <div className="relative" ref={notifRef}>
          <button
            onClick={() => setIsNotifOpen(!isNotifOpen)}
            className="relative p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 rounded-full border-2 border-white flex items-center justify-center">
                <span className="text-[10px] font-bold text-white">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              </span>
            )}
          </button>

          {isNotifOpen && (
            <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50 animate-fadeIn">
              <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                <div>
                  <h3 className="font-bold text-gray-900">Notifikasi</h3>
                  <p className="text-xs text-gray-500">
                    {unreadCount} belum dibaca
                  </p>
                </div>
                <div className="flex gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={onMarkAllRead}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                    >
                      <CheckCheck className="w-3.5 h-3.5" />
                      Tandai Dibaca
                    </button>
                  )}
                  <button
                    onClick={() => setIsNotifOpen(false)}
                    className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="max-h-[400px] overflow-y-auto">
                {displayNotifications.length > 0 ? (
                  displayNotifications.map((notif) => (
                    <button
                      key={notif.id}
                      onClick={() => handleNotificationClick(notif)}
                      className={`w-full p-4 text-left border-b border-gray-50 hover:bg-gray-50 transition-colors flex gap-3 ${
                        !notif.read ? "bg-indigo-50/50" : ""
                      } ${
                        notif.priority === "urgent"
                          ? "border-l-4 border-l-red-500"
                          : ""
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getIconBgClass(
                          notif.color
                        )}`}
                      >
                        {getIconComponent(notif.icon, notif.color)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p
                            className={`text-sm truncate ${
                              !notif.read
                                ? "font-bold text-gray-900"
                                : "font-medium text-gray-700"
                            }`}
                          >
                            {notif.title}
                          </p>
                          {!notif.read && (
                            <span className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0 mt-1.5"></span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                          {notif.message}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-1 font-medium">
                          {formatTimeAgo(notif.timestamp)}
                        </p>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="p-8 text-center">
                    <Bell className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                    <p className="text-sm text-gray-400 font-medium">
                      Belum ada notifikasi
                    </p>
                  </div>
                )}
              </div>

              {displayNotifications.length > 0 && (
                <div className="p-3 border-t border-gray-100 bg-gray-50">
                  <p className="text-[10px] text-gray-400 text-center font-medium">
                    Menampilkan {displayNotifications.length} notifikasi terbaru
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
